<script setup lang="ts">
import { computed, h } from 'vue'

import { ExternalLink } from '@lucide/vue'
import { keepPreviousData, useQuery } from '@tanstack/vue-query'
import { type ColumnDef, FlexRender, tableFeatures, useTable } from '@tanstack/vue-table'
import { useRouteQuery } from '@vueuse/router'

import ListToolbar from '#shared/components/ListToolbar.vue'
import PageHeader from '#shared/components/PageHeader.vue'
import RelativeDate from '#shared/components/RelativeDate.vue'
import SearchInput from '#shared/components/SearchInput.vue'
import TablePagination from '#shared/components/TablePagination.vue'
import TableState from '#shared/components/TableState.vue'
import { useOrganizations } from '#shared/composables/use-organizations'
import { useUsers } from '#shared/composables/use-users'
import { contextHubClient } from '#shared/services/api/context-hub.client'
import type { components } from '#shared/services/api/context-hub.schema'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#ui/table'
import { SELECT_ALL } from '#utils/constants'
import { ICONS } from '#utils/icons'
import { ROW_MOTION } from '#utils/motion'

type ConversationSession = components['schemas']['ConversationSessionDto']

definePage({
  meta: { requiresAuth: true, title: 'Chats' },
})

const orgsQuery = useOrganizations()
const { orgName } = orgsQuery
const { userLabel } = useUsers()

const pageSize = 50

const org = useRouteQuery<string>('org', '')
const page = useRouteQuery('page', '1', { transform: Number })
const search = useRouteQuery<string>('q', '')

const orgOptions = computed(() =>
  [...(orgsQuery.data.value ?? [])]
    .map((o) => ({ id: o.organizationId, name: o.name }))
    .sort((a, b) => a.name.localeCompare(b.name)),
)

function setOrg(value: unknown) {
  const v = typeof value === 'string' ? value : ''
  org.value = v === SELECT_ALL ? '' : v
  page.value = 1
}

const filtered = computed(() => !!org.value || !!search.value.trim())

function clearFilters() {
  org.value = ''
  search.value = ''
  page.value = 1
}

const conversationsQuery = useQuery({
  queryKey: computed(
    () => ['conversations', { org: org.value, page: page.value, pageSize }] as const,
  ),
  queryFn: async (): Promise<ConversationSession[]> => {
    const { data } = await contextHubClient.GET('/api/admin/conversations', {
      params: {
        query: {
          skip: (page.value - 1) * pageSize,
          take: pageSize + 1,
          organizationId: org.value || undefined,
        },
      },
    })
    return data ?? []
  },
  placeholderData: keepPreviousData,
})

const hasNextPage = computed(() => (conversationsQuery.data.value?.length ?? 0) > pageSize)
const pageItems = computed(() => (conversationsQuery.data.value ?? []).slice(0, pageSize))

const rows = computed(() => {
  const list = [...pageItems.value].sort((a, b) => {
    const at = a.lastActivityAt ? Date.parse(a.lastActivityAt) : 0
    const bt = b.lastActivityAt ? Date.parse(b.lastActivityAt) : 0
    return bt - at
  })
  const q = search.value.trim().toLowerCase()
  if (!q) return list
  return list.filter((r) =>
    [orgName(r.organizationId), userLabel(r.userId), r.userId, r.environment, r.conversationId]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q)),
  )
})

const intFmt = new Intl.NumberFormat()

function fmtInt(n: number | null | undefined): string {
  return n == null ? '—' : intFmt.format(n)
}

const features = tableFeatures({})

const columns: ColumnDef<typeof features, ConversationSession>[] = [
  {
    accessorKey: 'organizationId',
    header: 'Organization',
    cell: ({ row }) => h('span', { class: 'font-medium' }, orgName(row.original.organizationId)),
  },
  {
    accessorKey: 'userId',
    header: 'User',
    cell: ({ row }) =>
      h('span', { class: 'text-sm text-muted-foreground' }, userLabel(row.original.userId)),
  },
  {
    accessorKey: 'environment',
    header: 'Environment',
    cell: ({ row }) =>
      h('span', { class: 'text-sm text-muted-foreground' }, row.original.environment ?? '—'),
  },
  {
    accessorKey: 'turnCount',
    header: 'Turns',
    cell: ({ row }) => h('span', { class: 'tabular-nums' }, fmtInt(row.original.turnCount)),
  },
  {
    accessorKey: 'firstActivityAt',
    header: 'First activity',
    cell: ({ row }) => h(RelativeDate, { value: row.original.firstActivityAt }),
  },
  {
    accessorKey: 'lastActivityAt',
    header: 'Last activity',
    cell: ({ row }) => h(RelativeDate, { value: row.original.lastActivityAt }),
  },
  {
    id: 'langfuseUrl',
    header: 'Langfuse Url',
    cell: ({ row }) => {
      const url = row.original.langfuseSessionUrl
      if (!url) return null
      return h(
        'a',
        {
          'href': url,
          'target': '_blank',
          'rel': 'noopener noreferrer',
          'aria-label': 'Open Langfuse session',
          'title': 'Open Langfuse session',
          'class': 'inline-flex items-center text-primary hover:text-primary/80',
        },
        h(ExternalLink, { class: 'size-4' }),
      )
    },
  },
]

const table = useTable({
  features,
  data: computed(() => rows.value),
  columns,
})
</script>

<template>
  <PageHeader title="Chats" />

  <div class="flex flex-col gap-3">
    <ListToolbar
      noun="conversations"
      singular="conversation"
      :shown="conversationsQuery.isSuccess.value ? rows.length : undefined"
      :total="pageItems.length"
    >
      <Select :model-value="org || SELECT_ALL" @update:model-value="setOrg">
        <SelectTrigger class="w-[240px]" aria-label="Filter by organization">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="SELECT_ALL">All Organizations</SelectItem>
          <SelectItem v-for="o in orgOptions" :key="o.id" :value="o.id">
            {{ o.name }}
          </SelectItem>
        </SelectContent>
      </Select>
      <SearchInput
        v-model="search"
        label="Search conversations"
        placeholder="Search by user, environment or ID…"
        class="w-80"
      />
    </ListToolbar>

    <div class="-mx-3 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
            <TableHead v-for="header in headerGroup.headers" :key="header.id">
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableState
            :colspan="columns.length"
            :is-loading="conversationsQuery.isLoading.value"
            :error="conversationsQuery.error.value"
            :is-empty="!table.getRowModel().rows.length"
            :filtered="filtered"
            noun="conversations"
            :icon="ICONS.chats"
            @clear="clearFilters"
          >
            <template #description>
              Conversations appear here as customers use the assistant.
            </template>
          </TableState>
          <TransitionGroup v-bind="ROW_MOTION">
            <TableRow v-for="row in table.getRowModel().rows" :key="row.id">
              <TableCell v-for="cell in row.getAllCells()" :key="cell.id">
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </TableCell>
            </TableRow>
          </TransitionGroup>
        </TableBody>
      </Table>
    </div>

    <TablePagination
      v-model:page="page"
      :page-size="pageSize"
      :has-next="hasNextPage"
      noun="conversations"
    />
  </div>
</template>
